<script setup>
import { defineProps, computed, reactive, provide } from 'vue';
import { fromUnixTime, isSameDay, isThisYear, format } from 'date-fns';
import Message from './Message.vue';
import { MESSAGE_STATUS, MESSAGE_TYPES, SENDER_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import { GROUP_ROSTER_INDEX } from 'shared/helpers/lidMentions.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Boolean} isWhatsAppGroupConversation - Whether this is a WhatsApp group conversation
 * @property {Array} groupParticipants - WhatsApp group participant roster
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  isWhatsAppGroupConversation: {
    type: Boolean,
    default: false,
  },
  groupParticipants: {
    type: Array,
    default: () => [],
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const normalizeParticipantToken = value =>
  String(value || '').replace(/\D/g, '');

const groupParticipantIndex = computed(() => {
  const index = new Map();
  props.groupParticipants.forEach(participant => {
    const phone = normalizeParticipantToken(participant.phone);
    if (phone) index.set(phone, participant);
  });
  props.groupParticipants.forEach(participant => {
    const digits = normalizeParticipantToken(participant.digits);
    if (digits && !index.has(digits)) index.set(digits, participant);
  });
  return index;
});
// Bubbles (text + quoted preview) resolve @<lid> mention tokens against this
// live roster before the static LID map — see shared/helpers/lidMentions.js.
provide(GROUP_ROSTER_INDEX, groupParticipantIndex);

const isLeftGroupIncomingMessage = message => {
  if (
    !props.isWhatsAppGroupConversation ||
    message.messageType !== MESSAGE_TYPES.INCOMING ||
    message.private
  ) {
    return false;
  }

  const senderId = message.senderId ?? message.sender?.id;
  const senderType = message.sender?.type ?? message.senderType;
  if (!senderType || !senderId) return false;
  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return false;
  }

  return senderType.toLowerCase() !== SENDER_TYPES.USER.toLowerCase();
};

const parseGroupSenderHeader = content => {
  const match = String(content || '').match(/^\*\*([^\n]+?):\*\*(?:\n+|$)/);
  if (!match) return null;

  const label = match[1].trim();
  const delimiterIndex = label.indexOf(' - ');
  const hasDelimiter = delimiterIndex >= 0;
  const rawToken = hasDelimiter ? label.slice(0, delimiterIndex).trim() : '';
  const headerName = hasDelimiter
    ? label.slice(delimiterIndex + 3).trim()
    : label.slice(0, 40);

  return {
    rawToken,
    headerName,
    joinToken: normalizeParticipantToken(hasDelimiter ? rawToken : label),
    body: String(content || '').slice(match[0].length),
  };
};

const parsedPhone = rawToken => {
  if (!/^\+\d[\d\s()-]{5,}$/.test(rawToken)) return '';
  return normalizeParticipantToken(rawToken);
};

const rawMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

// Collapse consecutive exact-duplicate messages — same sender, same content, and
// same minute — into a single bubble. These are almost always a sync/relay hiccup
// (WhatsApp/Evolution re-delivering the same message), so we hide the copies
// silently rather than flagging "x2". Attachments/empty-content messages are left
// untouched (content alone can't tell them apart).
const allMessages = computed(() => {
  // Sort by real message time, not store/sync order — a late-synced old WhatsApp
  // message otherwise lands at the bottom by its recent id even though its date is
  // old, jumbling the thread. createdAt is the original message timestamp.
  const list = [...rawMessages.value].sort(
    (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
  );
  return list.filter((message, index) => {
    if (index === 0) return true;
    const previous = list[index - 1];
    const content = (message.content || '').trim();
    if (!content) return true;
    // Two images sent in the same minute share the Evolution-baked
    // "**<phone> - <name>:**" group header as their ENTIRE content, so a
    // content-only match would wrongly collapse distinct photos. The attachment
    // is the real payload — never treat an attachment-bearing message as a dupe.
    if (message.attachments?.length) return true;
    const sameSender =
      (message.senderId ?? message.sender?.id) ===
      (previous.senderId ?? previous.sender?.id);
    const sameMinute =
      message.createdAt &&
      previous.createdAt &&
      Math.floor(message.createdAt / 60) ===
        Math.floor(previous.createdAt / 60);
    const sameContent = content === (previous.content || '').trim();
    return !(sameSender && sameMinute && sameContent);
  });
});

const decoratedMessages = computed(() =>
  allMessages.value.map(message => {
    if (!isLeftGroupIncomingMessage(message)) return message;

    const parsed = parseGroupSenderHeader(message.content);
    if (!parsed) return { ...message, groupSender: null };

    const participant = groupParticipantIndex.value.get(parsed.joinToken);
    const rosterPhone = normalizeParticipantToken(participant?.phone);
    const headerPhone = parsedPhone(parsed.rawToken);
    const linkPhone = rosterPhone || headerPhone;
    let phoneLabel = '';
    if (rosterPhone) {
      phoneLabel =
        normalizeParticipantToken(parsed.rawToken) === rosterPhone
          ? parsed.rawToken
          : `+${rosterPhone}`;
    } else if (headerPhone) {
      phoneLabel = parsed.rawToken;
    }
    let displayName =
      parsed.headerName && parsed.headerName !== parsed.rawToken
        ? parsed.headerName
        : phoneLabel || parsed.headerName || parsed.rawToken;
    if (participant?.has_name) {
      displayName = participant.name;
    } else if (participant) {
      displayName =
        phoneLabel || parsed.headerName || participant.name || parsed.rawToken;
    }
    const hasLidOnlyIdentity =
      (!rosterPhone && participant?.id?.endsWith('@lid')) ||
      parsed.rawToken.toLowerCase().includes('@lid');
    let identityKey = `message:${message.id}`;
    if (!hasLidOnlyIdentity && participant) {
      identityKey = `participant:${
        participant.id || rosterPhone || parsed.joinToken
      }`;
    } else if (!hasLidOnlyIdentity && headerPhone) {
      identityKey = `participant:${headerPhone}`;
    }

    return {
      ...message,
      groupSender: {
        identityKey,
        displayName,
        avatarUrl: participant?.avatar || '',
        linkTarget: linkPhone ? { kind: 'wa', phone: linkPhone } : null,
        phoneLabel,
        body: parsed.body,
        matchedRoster: participant || null,
      },
    };
  })
);

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

const effectiveIdentityKey = message => {
  if (isLeftGroupIncomingMessage(message)) {
    return message.groupSender?.identityKey || `message:${message.id}`;
  }
  return message.senderId ?? message.sender?.id;
};

/**
 * Determines if a message should be visually grouped with the next message.
 * Group conversations use the participant identity while all other messages
 * retain the existing Chatwoot sender identity.
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === MESSAGE_STATUS.FAILED) return false;

  const currentIsGroupIncoming = isLeftGroupIncomingMessage(current);
  const nextIsGroupIncoming = isLeftGroupIncomingMessage(next);
  const hasSameSender =
    currentIsGroupIncoming || nextIsGroupIncoming
      ? currentIsGroupIncoming &&
        nextIsGroupIncoming &&
        effectiveIdentityKey(next) === effectiveIdentityKey(current)
      : effectiveIdentityKey(next) === effectiveIdentityKey(current);

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Determines whether participant identity chrome is shared with the previous
 * message. WhatsApp group identity spans minute boundaries but resets on a
 * participant, message-type, failure, or calendar-day boundary.
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether this message groups with the previous one
 */
const shouldGroupWithPrevious = (index, searchList) => {
  if (index === 0) return false;

  const current = searchList[index];
  const previous = searchList[index - 1];

  const currentIsGroupIncoming = isLeftGroupIncomingMessage(current);
  const previousIsGroupIncoming = isLeftGroupIncomingMessage(previous);

  if (currentIsGroupIncoming || previousIsGroupIncoming) {
    if (!currentIsGroupIncoming || !previousIsGroupIncoming) return false;
    if (
      current.status === MESSAGE_STATUS.FAILED ||
      previous.status === MESSAGE_STATUS.FAILED
    ) {
      return false;
    }

    return (
      effectiveIdentityKey(current) === effectiveIdentityKey(previous) &&
      current.messageType === previous.messageType &&
      isSameDay(
        fromUnixTime(current.createdAt),
        fromUnixTime(previous.createdAt)
      )
    );
  }

  const currentSenderId = current.senderId ?? current.sender?.id;
  const previousSenderId = previous.senderId ?? previous.sender?.id;

  return (
    currentSenderId === previousSenderId &&
    current.messageType === previous.messageType
  );
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};

/**
 * Whether a WhatsApp-style day separator should precede this message, i.e. it is
 * the first message or falls on a different calendar day than the previous one.
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean}
 */
const shouldShowDateSeparator = (index, searchList) => {
  if (index === 0) return true;
  const current = searchList[index]?.createdAt;
  const previous = searchList[index - 1]?.createdAt;
  if (!current || !previous) return false;
  return !isSameDay(fromUnixTime(current), fromUnixTime(previous));
};

/**
 * Label for a day separator, e.g. "29 Jun" (current year) or "29 Jun 2025".
 * @param {Number} createdAt - Unix timestamp (seconds)
 * @returns {String}
 */
const dateSeparatorLabel = createdAt => {
  const date = fromUnixTime(createdAt);
  return format(date, isThisYear(date) ? 'd MMM' : 'd MMM yyyy');
};
</script>

<template>
  <ul class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="(message, index) in decoratedMessages" :key="message.id">
      <div
        v-if="shouldShowDateSeparator(index, decoratedMessages)"
        class="flex justify-center my-3"
      >
        <span
          class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
        >
          {{ dateSeparatorLabel(message.createdAt) }}
        </span>
      </div>
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, decoratedMessages)"
        :group-with-previous="shouldGroupWithPrevious(index, decoratedMessages)"
        :is-whats-app-group-conversation="isWhatsAppGroupConversation"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
